export const UFOV_LANGUAGE_OPTIONS = [
    { id: 'en', label: 'English', direction: 'ltr', appStoreLocale: 'en-US', playLocale: 'en-US' },
    { id: 'fr', label: 'Français', direction: 'ltr', appStoreLocale: 'fr-FR', playLocale: 'fr-FR' },
    { id: 'es', label: 'Español', direction: 'ltr', appStoreLocale: 'es-ES', playLocale: 'es-ES' },
    { id: 'pt', label: 'Português', direction: 'ltr', appStoreLocale: 'pt-BR', playLocale: 'pt-BR' },
    { id: 'it', label: 'Italiano', direction: 'ltr', appStoreLocale: 'it', playLocale: 'it-IT' },
    { id: 'de', label: 'Deutsch', direction: 'ltr', appStoreLocale: 'de-DE', playLocale: 'de-DE' },
    { id: 'ja', label: '日本語', direction: 'ltr', appStoreLocale: 'ja', playLocale: 'ja-JP' },
    { id: 'ko', label: '한국어', direction: 'ltr', appStoreLocale: 'ko', playLocale: 'ko-KR' },
    { id: 'ar', label: 'العربية', direction: 'rtl', appStoreLocale: 'ar-SA', playLocale: 'ar' },
    { id: 'sv', label: 'Svenska', direction: 'ltr', appStoreLocale: 'sv', playLocale: 'sv-SE' },
    { id: 'no', label: 'Norsk', direction: 'ltr', appStoreLocale: 'no', playLocale: 'no-NO' },
    { id: 'da', label: 'Dansk', direction: 'ltr', appStoreLocale: 'da', playLocale: 'da-DK' },
    { id: 'nl', label: 'Nederlands', direction: 'ltr', appStoreLocale: 'nl-NL', playLocale: 'nl-NL' },
    { id: 'pl', label: 'Polski', direction: 'ltr', appStoreLocale: 'pl', playLocale: 'pl-PL' },
    { id: 'tr', label: 'Türkçe', direction: 'ltr', appStoreLocale: 'tr', playLocale: 'tr-TR' },
    { id: 'zh-hans', label: '简体中文', direction: 'ltr', appStoreLocale: 'zh-Hans', playLocale: 'zh-CN' },
    { id: 'zh-hant', label: '繁體中文', direction: 'ltr', appStoreLocale: 'zh-Hant', playLocale: 'zh-TW' },
    { id: 'ru', label: 'Русский', direction: 'ltr', appStoreLocale: 'ru', playLocale: 'ru-RU' },
    { id: 'id', label: 'Bahasa Indonesia', direction: 'ltr', appStoreLocale: 'id', playLocale: 'id' },
    { id: 'hi', label: 'हिन्दी', direction: 'ltr', appStoreLocale: 'hi', playLocale: 'hi-IN' },
];

export const UFOV_LANGUAGE_IDS = UFOV_LANGUAGE_OPTIONS.map(({ id }) => id);

const LANGUAGE_ALIASES = {
    nb: 'no',
    nn: 'no',
    'no-nb': 'no',
    'pt-br': 'pt',
    'pt-pt': 'pt',
    'zh-cn': 'zh-hans',
    'zh-sg': 'zh-hans',
    'zh-hans-cn': 'zh-hans',
    'zh-hans-sg': 'zh-hans',
    'zh-tw': 'zh-hant',
    'zh-hk': 'zh-hant',
    'zh-mo': 'zh-hant',
    'zh-hant-tw': 'zh-hant',
    'zh-hant-hk': 'zh-hant',
};

const normalizeTag = (value) => `${value || ''}`.trim().replaceAll('_', '-').toLowerCase();

const getPreferredLanguageCandidates = (preferredLanguages) => (
    Array.isArray(preferredLanguages)
        ? preferredLanguages
        : (
            typeof navigator !== 'undefined'
                ? [...(navigator.languages || []), navigator.language]
                : []
        )
);

export const normalizeUFOVLanguage = (language, fallback = 'en') => {
    const tag = normalizeTag(language);

    if (!tag) {
        return fallback;
    }

    if (UFOV_LANGUAGE_IDS.includes(tag)) {
        return tag;
    }

    if (LANGUAGE_ALIASES[tag]) {
        return LANGUAGE_ALIASES[tag];
    }

    if (tag.startsWith('zh-')) {
        return /(?:hant|tw|hk|mo)/.test(tag) ? 'zh-hant' : 'zh-hans';
    }

    const base = tag.split('-')[0];

    return LANGUAGE_ALIASES[base]
        || (UFOV_LANGUAGE_IDS.includes(base) ? base : fallback);
};

export const detectPreferredUFOVLanguage = (preferredLanguages) => {
    const candidates = getPreferredLanguageCandidates(preferredLanguages);

    for (const candidate of candidates) {
        const normalized = normalizeUFOVLanguage(candidate, '');

        if (normalized) {
            return normalized;
        }
    }

    return 'en';
};

export const isIndonesianUFOVAudience = (selectedLanguage, preferredLanguages) => {
    if (normalizeUFOVLanguage(selectedLanguage, '') === 'id') {
        return true;
    }

    return getPreferredLanguageCandidates(preferredLanguages).some((candidate) => {
        const tag = normalizeTag(candidate);

        if (!tag) {
            return false;
        }

        if (normalizeUFOVLanguage(tag, '') === 'id') {
            return true;
        }

        if (typeof Intl !== 'undefined' && typeof Intl.Locale === 'function') {
            try {
                const locale = new Intl.Locale(tag);
                return locale.language === 'id' || locale.region === 'ID';
            } catch (error) {
                // Fall through to the compact locale-tag check below.
            }
        }

        return /(?:^|-)id(?:-|$)/.test(tag);
    });
};

export const getUFOVLanguageDirection = (language) => (
    UFOV_LANGUAGE_OPTIONS.find(({ id }) => id === normalizeUFOVLanguage(language))?.direction || 'ltr'
);
